import { Injectable } from '@angular/core';
import { AppStateService } from './app-state.service';

@Injectable()
export class DateTimeService {
  static dateOption = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  } as const;
  static timeOption = {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  } as const;

  constructor() {}

  //#region date

  static getTodayDateTime(): string {
    const todayDate = DateTimeService.convertTZ(
      new Date(AppStateService.serverDateTime)
    );
    return todayDate.toString();
  }

  static getTodayDate(): string {
    const todayDate = DateTimeService.convertTZ(
      new Date(AppStateService.serverDateTime)
    );
    todayDate.setHours(0);
    todayDate.setMinutes(0);
    todayDate.setSeconds(0);

    return todayDate.toString();
  }

  static getCurrentDateTime() {
    const todayDate = DateTimeService.convertTZ(
      new Date(AppStateService.serverDateTime)
    );
    return new Date(
      todayDate.getTime() - todayDate.getTimezoneOffset() * 60000
    ).toISOString();
  }

  //this method gets one year later from today's date
  static getAYearLater(): string {
    const d = new Date();
    const year = d.getFullYear();
    const month = d.getMonth();
    const day = d.getDate();
    const newDate = new Date(year + 1, month, day);
    return this.convertFarsiNumToLatinNum(
      newDate.toLocaleDateString('en-US', this.dateOption)
    );
  }

  static getAYearLaterToIsoString(): string {
    const d = new Date();
    const year = d.getFullYear();
    const month = d.getMonth();
    const day = d.getDate();
    const yearLater = new Date(year + 1, month, day, 0, 0);
    let fixedDate = new Date(
      yearLater.getTime() - yearLater.getTimezoneOffset() * 60000
    );
    return this.convertFarsiNumToLatinNum(fixedDate.toISOString());
  }

  static getAYearLaterUTC(): string {
    const d = new Date();
    const year = d.getFullYear();
    const month = d.getMonth();
    const day = d.getDate();
    const nextYear = new Date(year + 1, month, day);
    return nextYear.toString();
  }

  static getSimpleTodayDate(): string {
    const todayDate = DateTimeService.convertTZ(
      new Date(AppStateService.serverDateTime)
    );
    todayDate.setHours(23);
    todayDate.setMinutes(59);
    todayDate.setSeconds(59);
    return todayDate.toString();
  }

  //this method gets ten days ago from today's date
  static getSomeDaysAgo(x: number, isMidnight: boolean = false): string {
    const todayDate = DateTimeService.convertTZ(
      new Date(AppStateService.serverDateTime)
    );
    if (!isMidnight) {
      todayDate.setHours(23);
      todayDate.setMinutes(59);
      todayDate.setSeconds(59);
    } else {
      todayDate.setHours(0);
      todayDate.setMinutes(0);
      todayDate.setSeconds(0);
    }
    todayDate.setDate(todayDate.getDate() - x);
    return todayDate.toString();
  }

  static getSomeDaysAfter(daysAfter: number): string {
    const todayDate = DateTimeService.convertTZ(
      new Date(AppStateService.serverDateTime)
    );

    const daysLater = new Date(
      todayDate.getFullYear(),
      todayDate.getMonth(),
      todayDate.getDate() + daysAfter,
      todayDate.getHours(),
      todayDate.getMinutes(),
      todayDate.getSeconds()
    );

    const fixedDate = new Date(
      daysLater.getTime() - daysLater.getTimezoneOffset() * 60000
    ).toISOString();
    return fixedDate;
  }

  static addTimeToDate(inputDate: any, inputTime: any): any {
    inputTime = this.addSecondsToTime(inputTime).toString();
    inputTime = inputTime?.split(':');

    inputDate.setHours(Number(inputTime[0]));
    inputDate.setMinutes(Number(inputTime[1]));
    inputDate.setSeconds(Number(inputTime[2]));

    return inputDate;
  }

  static toUTC(inputDate: any): any {
    let date = inputDate;
    if (inputDate?._d) date = inputDate?._d;

    if (!date) return inputDate;

    date = date.toString();
    return date.toUTC();
  }

  static shamsiToUTC(date): string {
    return date.shamsiToUTC();
  }

  static utcToLocalShamsiDate(dateTime: string): string {
    if (!this.isValidUtcDateTime(dateTime)) return '-';
    const DATE_TIME = new Date(dateTime);
    return this.convertFarsiNumToLatinNum(
      DATE_TIME.toLocaleDateString('fa-IR', this.dateOption)
    );
  }

  static utcToLocalDate(dateTime: string): string {
    if (!this.isValidUtcDateTime(dateTime)) return '-';
    const DATE_TIME = new Date(dateTime);
    return this.convertFarsiNumToLatinNum(
      DATE_TIME.toLocaleDateString('en-US', this.dateOption)
    );
  }

  static getCurrentShamsiYear(): string {
    const date = new Date().toLocaleDateString('fa-IR', { year: 'numeric' });
    return this.convertFarsiNumToLatinNum(date);
  }

  static getCurrentShamsiMonth(): string {
    const date = new Date().toLocaleDateString('fa-IR', { month: "numeric" });
    return this.convertFarsiNumToLatinNum(date); 
  }

  //#endregion

  //#region time

  static getTime(): string {
    const todayDate = DateTimeService.convertTZ(AppStateService.serverDateTime);
    const hour = String(todayDate.getHours()).padStart(2, '0');
    const minutes = String(todayDate.getMinutes()).padStart(2, '0');
    return `${hour}:${minutes}`;
  }

  static getCustomTime(minuteAhead = 1): string {
    const todayDate = DateTimeService.convertTZ(
      new Date(AppStateService.serverDateTime)
    );
    let minute =
      (todayDate.getMinutes() < 9 ? '0' : '') +
      (+todayDate.getMinutes() + minuteAhead).toString();
    let hour = todayDate.getHours().toString();
    hour = ('0' + hour).slice(-2);
    if (+minute > 59) {
      minute = '0' + (minuteAhead - 1).toString();
      hour = (+hour + 1 < 24 ? +hour + 1 : 0).toString();
    }
    return hour + ':' + minute;
  }

  static combiningTime(time: string): any {
    let timeArray = time?.split(':');
    return timeArray;
  }

  static addSecondsToTime(time: any): any {
    if (time) return time + ':00';
    else return null;
  }

  static removeTimeSeconds(time: any): any {
    if (time) return time.substring(0, 5);
    else return null;
  }

  static utcToLocalShamsiTime(dateTime: string): string {
    if (!this.isValidUtcDateTime(dateTime)) return '-';
    const DATE_TIME = new Date(dateTime);
    return this.convertFarsiNumToLatinNum(
      DATE_TIME.toLocaleTimeString('fa-IR', this.timeOption)
    );
  }

  static utcToLocalTime(dateTime: string): string {
    if (!this.isValidUtcDateTime(dateTime)) return '-';
    const DATE_TIME = new Date(dateTime);
    return this.convertFarsiNumToLatinNum(
      DATE_TIME.toLocaleTimeString('en-US', this.timeOption)
    );
  }

  // dayjs.Dayjs
  static addSeconds(date, seconds): any {
    date.setSeconds(date.getSeconds() + seconds);
    // const Date = dayjs(date).utc().add(1, "s");
    // return Date;
    return date;
  }
  //#endregion

  //#region dateTime

  static combineDateTimeToUTC(date: string, time: string): string {
    let [hour, minute] = time?.split(':');

    if (+hour < 10) {
      if (hour[0] != '0') hour = '0' + hour;
    }

    let fullTime = hour + ':' + minute;
    let dateTime = date.replace('T00:00', 'T' + fullTime);
    dateTime = dateTime.split('.')[0];

    if (!date || !fullTime) return;

    if (dateTime == 'Invalid date') dateTime = this.getTodayDate().toUTC();

    return dateTime;
  }

  static separateDateTimeUTC(date: string): [string, string] {
    let array = date?.split('T');
    let newDate = array[0];
    let time = array[1]?.substring(0, 5);

    return [newDate, time];
  }

  static utcToLocalShamsiDateTime(dateTime: string): string {
    if (!this.isValidUtcDateTime(dateTime)) return '-';
    const DATE_TIME = new Date(dateTime);
    let date = this.convertFarsiNumToLatinNum(
      DATE_TIME.toLocaleDateString('fa-IR', this.dateOption)
    );
    let time = this.convertFarsiNumToLatinNum(
      DATE_TIME.toLocaleTimeString('fa-IR', this.timeOption).substring(0, 5)
    );
    return date + ' - ' + time;
  }

  static utcToLocalShamsiDateTimeWithSecond(dateTime: string): string {
    if (!this.isValidUtcDateTime(dateTime)) return '-';
    const DATE_TIME = new Date(dateTime);
    let date = this.convertFarsiNumToLatinNum(
      DATE_TIME.toLocaleDateString('fa-IR', this.dateOption)
    );
    let time = this.convertFarsiNumToLatinNum(
      DATE_TIME.toLocaleTimeString('fa-IR', this.timeOption)
    );
    return date + ' - ' + time;
  }

  static utcToLocalShamsiDateTimeWithSpace(dateTime: string): string {
    if (!this.isValidUtcDateTime(dateTime)) return '-';
    const DATE_TIME = new Date(dateTime);
    let date = this.convertFarsiNumToLatinNum(
      DATE_TIME.toLocaleDateString('fa-IR', this.dateOption)
    );
    let time = this.convertFarsiNumToLatinNum(
      DATE_TIME.toLocaleTimeString('fa-IR', this.timeOption)
    );
    return date + '<br>' + time;
  }

  static utcToLocalDateTime(dateTime: string): string {
    if (!this.isValidUtcDateTime(dateTime)) return '-';
    const DATE_TIME = new Date(dateTime);
    let date = this.convertFarsiNumToLatinNum(
      DATE_TIME.toLocaleDateString('en-US', this.dateOption)
    );
    let time = this.convertFarsiNumToLatinNum(
      DATE_TIME.toLocaleTimeString('en-US', this.timeOption).substring(0, 5)
    );
    return date + ' - ' + time;
  }

  static utcToLocalDateTimeWithSecond(dateTime: string): string {
    if (!this.isValidUtcDateTime(dateTime)) return '-';
    const DATE_TIME = new Date(dateTime);
    let date = this.convertFarsiNumToLatinNum(
      DATE_TIME.toLocaleDateString('en-US', this.dateOption)
    );
    let time = this.convertFarsiNumToLatinNum(
      DATE_TIME.toLocaleTimeString('en-US', this.timeOption)
    );
    return date + ' - ' + time;
  }

  static utcToLocalDateTimeWithSpace(dateTime: string): string {
    if (!this.isValidUtcDateTime(dateTime)) return '-';
    const DATE_TIME = new Date(dateTime);
    let date = this.convertFarsiNumToLatinNum(
      DATE_TIME.toLocaleDateString('en-US', this.dateOption)
    );
    let time = this.convertFarsiNumToLatinNum(
      DATE_TIME.toLocaleTimeString('en-US', this.timeOption)
    );
    return date + '<br>' + time;
  }
  //#endregion

  static convertFarsiNumToLatinNum(nums: string): string {
    return nums.replace(/([۰-۹])/g, (token) =>
      String.fromCharCode(token.charCodeAt(0) - 1728)
    );
  }

  static isValidUtcDateTime(dateTime: string): boolean {
    if (dateTime == '0001-01-01T00:00:00Z' || dateTime == '0001-01-01T00:00:00')
      return false;
    return true;
  }

  static getUtcString(dateTime: string): string {
    let array = dateTime.split('T');
    let time = array[1];
    let date = array[0].split('-');
    let year = date[0];
    let month = date[1];
    let day = date[2];
    let fullDate = `${month}/${day}/${year}`;

    return `${fullDate} ${time} UTC`;
  }

  static convertTZ(date) {
    const d = new Date(date);
    d.setHours(d.getHours() + 3);
    d.setMinutes(d.getMinutes() + 30);
    const result = new Date(d.toISOString().slice(0, -1));
    return result;
  }
}
